// Log in (AUTH-001, API_CONTRACTS §3.1). Owner design (2026-10-21): live hosts show Google only (OIDC
// redirect). The email code and passkey forms remain for local testing only (localhost or the mock
// build), where Google cannot run; the API still serves them. OTP start never reveals whether an
// account exists.
import { useRef, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { browserSupportsWebAuthn, startAuthentication } from "@simplewebauthn/browser";
import { t } from "@ady/i18n";
import { Banner, BrandIcon, Button, Field, focusFirstInvalid, Input } from "@ady/ui";
import { api } from "../api/client";
import { errorMessage } from "../api/errors";
import { keys } from "../api/queries";
import { isMocked } from "../shell/context";
import { useGo } from "../shell/AppLink";
import { TID } from "../testids";
import { AuthLayout } from "./AuthLayout";
import { LoginShowcase } from "./LoginShowcase";

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function nextPath(): string {
  const next = new URLSearchParams(location.search).get("next");
  // Same-origin relative paths only (no open redirect).
  return next && next.startsWith("/") && !next.startsWith("//") && !next.includes("\\")
    ? next
    : "/";
}

/** Email and passkey log-in stay available on localhost and the mock build only (tests, local work). */
export function localLoginAllowed(): boolean {
  return isMocked() || /^(localhost|127\.0\.0\.1|\[::1\])$/.test(location.hostname);
}

export function SignInPage() {
  const local = localLoginAllowed();
  // D-067: keep a referral code from a shop's link (/sign-in?ref=CODE) for the onboarding form.
  try {
    const ref = new URLSearchParams(location.search).get("ref");
    if (ref) sessionStorage.setItem("ady.referral", ref.slice(0, 32));
  } catch {
    // no storage: the shop types the code itself
  }
  const [email, setEmail] = useState("");
  const [fieldError, setFieldError] = useState<string>();
  // A failed Google round trip comes back as ?auth_error=google (the reason is only logged server-side).
  const [formError, setFormError] = useState<string | undefined>(() =>
    new URLSearchParams(location.search).get("auth_error") === "google"
      ? t("auth.googleFailed")
      : undefined,
  );
  const [sending, setSending] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const go = useGo();
  const qc = useQueryClient();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(undefined);
    const value = email.trim();
    if (!EMAIL.test(value)) {
      setFieldError(t("auth.emailInvalid"));
      requestAnimationFrame(() => focusFirstInvalid(formRef.current));
      return;
    }
    setFieldError(undefined);
    setSending(true);
    try {
      const res = await api.otpStart({ channel: "email", value, purpose: "signin" });
      const q = new URLSearchParams({
        email: value,
        challenge: res.challenge_id,
        resend: String(res.resend_after_s),
        next: nextPath(),
      });
      go(`/sign-in/verify?${q.toString()}`);
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setSending(false);
    }
  };

  const google = () => {
    if (isMocked()) {
      setFormError(t("auth.googleNeedsServer"));
      return;
    }
    // Come back where sign-in was asked for (an invitation, the Import Helper, MCP connect): Google
    // sign-in used to drop `next`, so an invitee without a shop landed on onboarding (2026-11-04).
    // The server keeps only a same-site path (safeReturnTo).
    const next = nextPath();
    location.assign(
      next === "/"
        ? api.googleStartUrl
        : `${api.googleStartUrl}?return_to=${encodeURIComponent(next)}`,
    );
  };

  const passkey = async () => {
    setFormError(undefined);
    if (!browserSupportsWebAuthn()) {
      setFormError(t("auth.passkeyUnsupported"));
      return;
    }
    setPasskeyBusy(true);
    try {
      const opts = await api.passkeyOptions("signin");
      const response = await startAuthentication({ optionsJSON: opts.options as never });
      await api.passkeyAssert(opts.challenge_id, response);
      await qc.invalidateQueries({ queryKey: keys.me });
      go(nextPath());
    } catch (err) {
      setFormError(
        err instanceof Error && err.name === "NotAllowedError"
          ? t("auth.passkeyCancelled")
          : errorMessage(err),
      );
    } finally {
      setPasskeyBusy(false);
    }
  };

  return (
    <AuthLayout
      title={t("auth.signInTitle")}
      lead={t("auth.signInLead")}
      testId={TID.signInPage}
      showcase={<LoginShowcase />}
    >
      {formError ? <Banner kind="error" title={formError} data-testid={TID.formError} /> : null}
      {nextPath().startsWith("/go/import/") ? (
        <Banner kind="info" title={t("importMarket.helper.signIn")} />
      ) : null}
      <button
        type="button"
        onClick={google}
        data-testid={TID.googleButton}
        className="flex min-h-control w-full items-center justify-center gap-2.5 rounded-sm border border-control bg-surface text-sm font-semibold shadow-1 hover:bg-hover"
      >
        <BrandIcon brand="google" size={16} />
        {t("auth.google")}
      </button>
      {local ? (
        <section
          aria-label={t("auth.localOnly")}
          className="flex flex-col gap-3 rounded-sm border border-dashed border-control p-3"
        >
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-bold tracking-wide text-fg-tertiary uppercase">
              {t("auth.localOnly")}
            </span>
            <span className="text-xs text-fg-tertiary">{t("auth.localOnlyHelp")}</span>
          </div>
          <form ref={formRef} onSubmit={submit} noValidate className="flex flex-col gap-3">
            <Field label={t("auth.emailLabel")} error={fieldError} required>
              <Input
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={TID.emailInput}
              />
            </Field>
            <Button
              icon="send"
              type="submit"
              fullWidth
              loading={sending}
              loadingLabel={t("auth.sendingCode")}
              data-testid={TID.sendCode}
            >
              {t("auth.sendCode")}
            </Button>
          </form>
          <Button
            variant="secondary"
            fullWidth
            icon="fingerprint"
            onClick={passkey}
            loading={passkeyBusy}
            data-testid={TID.passkeyButton}
          >
            {t("auth.passkey")}
          </Button>
        </section>
      ) : null}
    </AuthLayout>
  );
}
